import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, Printer, Download, User, Phone, Envelope, Plus, FloppyDisk, PaperPlaneTilt } from "@phosphor-icons/react";

/**
 * Generic PDF module page.
 * Props:
 *  - moduleId, title, subtitle, icon
 *  - endpoint: backend path, e.g. "/modules/office-license/pdf"
 *  - filenamePrefix
 *  - fields: [{ key, label, type: 'text'|'number'|'select'|'textarea', required, mono, options: [{v,l}], placeholder, help, colSpan? }]
 *  - defaults: {key:value}
 *  - emailSubjectBuilder(customer, form): string
 *  - emailBodyBuilder(customer, form): string
 */
export default function GenericModulePage({
  moduleId,
  title,
  subtitle,
  icon: Icon,
  endpoint,
  filenamePrefix,
  fields,
  defaults = {},
  emailSubjectBuilder,
  emailBodyBuilder,
}) {
  useDocumentTitle(`Modulo · ${title}`);
  const nav = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [q, setQ] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [form, setForm] = useState(defaults);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [newCustomerDialog, setNewCustomerDialog] = useState(false);

  const loadCustomers = async () => {
    setLoading(true);
    const { data } = await api.get("/customers");
    setCustomers(data);
    setLoading(false);
    return data;
  };

  useEffect(() => { loadCustomers(); }, []);

  const selectedCustomer = useMemo(
    () => customers.find((c) => c.id === customerId) || null,
    [customers, customerId]
  );

  const filteredCustomers = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return customers;
    return customers.filter((c) =>
      `${c.surname} ${c.name} ${c.phone} ${c.email || ""} ${c.ragione_sociale || ""}`.toLowerCase().includes(query)
    );
  }, [customers, q]);

  const validate = () => {
    if (!customerId) return "Seleziona un cliente";
    for (const f of fields) {
      if (f.required) {
        const v = form[f.key];
        if (v === undefined || v === null || String(v).trim() === "") return `${f.label} richiesto`;
        if (f.type === "number" && (!parseInt(v, 10) || parseInt(v, 10) <= 0)) return `${f.label} > 0`;
      }
    }
    return null;
  };

  const buildPayload = () => {
    const payload = { customer_id: customerId };
    for (const f of fields) {
      let v = form[f.key];
      if (v === undefined || v === null || v === "") {
        payload[f.key] = null;
        continue;
      }
      if (f.type === "number") v = parseInt(v, 10);
      payload[f.key] = typeof v === "string" ? v.trim() : v;
    }
    return payload;
  };

  const generatePdf = async () => {
    const err = validate();
    if (err) { toast.error(err); return null; }
    setBusy(true);
    try {
      const { data } = await api.post(endpoint, buildPayload(), { responseType: "blob" });
      return new Blob([data], { type: "application/pdf" });
    } catch (e) {
      let msg = "Errore generazione PDF";
      try {
        if (e.response?.data instanceof Blob) {
          const t = await e.response.data.text();
          msg = formatApiErrorDetail(JSON.parse(t).detail) || msg;
        } else {
          msg = formatApiErrorDetail(e.response?.data?.detail) || msg;
        }
      } catch { /* ignore */ }
      toast.error(msg);
      return null;
    } finally {
      setBusy(false);
    }
  };

  const filename = () => `${filenamePrefix}-${(selectedCustomer?.surname || "cliente").toLowerCase()}.pdf`;

  const download = async () => {
    const blob = await generatePdf();
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename();
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success("PDF scaricato");
  };

  const print = async () => {
    const blob = await generatePdf();
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const w = window.open(url);
    if (w) {
      w.addEventListener("load", () => {
        try { w.focus(); w.print(); } catch { /* ignore */ }
      });
    } else {
      toast.error("Popup bloccato: consenti la finestra per stampare");
    }
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };

  const sendByEmail = async () => {
    if (!selectedCustomer?.email) {
      toast.error("Il cliente non ha un'email registrata");
      return;
    }
    const blob = await generatePdf();
    if (!blob) return;
    // Download first so user can attach it
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename();
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    // Open mailto with prefill
    const subject = encodeURIComponent(
      emailSubjectBuilder ? emailSubjectBuilder(selectedCustomer, form) : `${title} - Diotech`
    );
    const body = encodeURIComponent(
      emailBodyBuilder
        ? emailBodyBuilder(selectedCustomer, form)
        : `Gentile ${selectedCustomer.surname} ${selectedCustomer.name},\n\nin allegato trova il documento richiesto.`
    );
    setTimeout(() => {
      window.location.href = `mailto:${selectedCustomer.email}?subject=${subject}&body=${body}`;
      toast.info("PDF scaricato. Trascinalo nell'email che si sta aprendo per allegarlo.", { duration: 6000 });
    }, 200);
  };

  return (
    <div className="p-6 lg:p-10 max-w-3xl mx-auto" data-testid={`module-page-${moduleId}`}>
      <Link to="/moduli" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-4">
        <ArrowLeft size={14} /> Elenco moduli
      </Link>

      <header className="mb-6">
        <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-1">// MODULO</div>
        <h1 className="text-3xl font-display font-bold tracking-tight flex items-center gap-3">
          {Icon && <Icon size={28} weight="duotone" className="text-primary" />}
          {title}
        </h1>
        {subtitle && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}
      </header>

      <Card className="p-6 border-border/70 rounded-lg space-y-5">
        {/* Customer selection + Nuovo cliente */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <Label className="font-mono text-[10px] uppercase tracking-widest">Cliente</Label>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setNewCustomerDialog(true)}
              className="rounded-full gap-1 text-xs"
              data-testid="module-new-customer-btn"
            >
              <Plus size={13} /> Nuovo cliente
            </Button>
          </div>
          <Select value={customerId} onValueChange={setCustomerId}>
            <SelectTrigger data-testid="module-customer-select">
              <SelectValue placeholder={loading ? "Caricamento…" : "Seleziona un cliente registrato"} />
            </SelectTrigger>
            <SelectContent className="max-h-96">
              <div className="p-2 sticky top-0 bg-popover z-10 border-b border-border/50">
                <Input
                  placeholder="Cerca cliente…"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  className="h-8"
                  data-testid="module-customer-search"
                  onKeyDown={(e) => e.stopPropagation()}
                />
              </div>
              {filteredCustomers.length === 0 && (
                <div className="p-4 text-center text-xs text-muted-foreground">Nessun cliente</div>
              )}
              {filteredCustomers.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.surname} {c.name} · {c.phone}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {selectedCustomer && (
            <div className="mt-3 p-3 rounded-md border border-border/60 bg-secondary/40 text-sm" data-testid="selected-customer-preview">
              <div className="flex items-center gap-2 mb-1">
                <User size={14} weight="duotone" className="text-primary" />
                <div className="font-medium">{selectedCustomer.surname} {selectedCustomer.name}</div>
                {selectedCustomer.ragione_sociale && (
                  <Badge className="bg-secondary text-foreground border-border hover:bg-secondary font-mono text-[9px] uppercase">
                    {selectedCustomer.ragione_sociale}
                  </Badge>
                )}
              </div>
              <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground font-mono">
                <span className="inline-flex items-center gap-1"><Phone size={11} /> {selectedCustomer.phone}</span>
                {selectedCustomer.email && <span className="inline-flex items-center gap-1"><Envelope size={11} /> {selectedCustomer.email}</span>}
                {selectedCustomer.piva && <span>P.IVA: {selectedCustomer.piva}</span>}
              </div>
            </div>
          )}
        </div>

        {/* Fields */}
        <div className="border-t border-border/50 pt-5">
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-3">// DATI MODULO</div>
          <div className="grid sm:grid-cols-2 gap-4">
            {fields.map((f) => (
              <div key={f.key} className={f.colSpan === 2 || f.type === "textarea" ? "sm:col-span-2" : ""}>
                <Label className="font-mono text-[10px] uppercase tracking-widest">
                  {f.label} {!f.required && <span className="text-muted-foreground/70 normal-case">(opzionale)</span>}
                </Label>
                {f.type === "select" ? (
                  <Select value={String(form[f.key] || "")} onValueChange={(v) => setForm({ ...form, [f.key]: v })}>
                    <SelectTrigger className="mt-1.5" data-testid={`field-${f.key}`}><SelectValue placeholder={f.placeholder || "Seleziona…"} /></SelectTrigger>
                    <SelectContent>
                      {f.options.map((o) => (
                        <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : f.type === "textarea" ? (
                  <Textarea
                    rows={3}
                    value={form[f.key] || ""}
                    onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                    placeholder={f.placeholder}
                    data-testid={`field-${f.key}`}
                    className={`mt-1.5 ${f.mono ? "font-mono" : ""}`}
                  />
                ) : (
                  <Input
                    type={f.type || "text"}
                    min={f.type === "number" ? "1" : undefined}
                    step={f.type === "number" ? "1" : undefined}
                    value={form[f.key] ?? ""}
                    onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                    placeholder={f.placeholder}
                    data-testid={`field-${f.key}`}
                    className={`mt-1.5 ${f.mono ? "font-mono" : ""}`}
                  />
                )}
                {f.help && <div className="text-[10px] text-muted-foreground mt-1 font-mono">{f.help}</div>}
              </div>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="border-t border-border/50 pt-4 flex flex-col sm:flex-row gap-2 justify-end flex-wrap">
          <Button
            variant="outline"
            onClick={sendByEmail}
            disabled={busy || !selectedCustomer?.email}
            className="rounded-full gap-2"
            data-testid="module-email-btn"
            title={!selectedCustomer?.email ? "Il cliente selezionato non ha email" : ""}
          >
            <PaperPlaneTilt size={16} /> Invia via email
          </Button>
          <Button variant="outline" onClick={print} disabled={busy} className="rounded-full gap-2" data-testid="module-print-btn">
            <Printer size={16} /> Stampa
          </Button>
          <Button onClick={download} disabled={busy} className="rounded-full gap-2" data-testid="module-download-btn">
            <Download size={16} /> {busy ? "Attendi…" : "Scarica PDF"}
          </Button>
        </div>
      </Card>

      {/* Quick new customer dialog */}
      <QuickCustomerDialog
        open={newCustomerDialog}
        onOpenChange={setNewCustomerDialog}
        onCreated={async (id) => {
          await loadCustomers();
          setCustomerId(id);
          setNewCustomerDialog(false);
          toast.success("Cliente creato e selezionato");
        }}
      />
    </div>
  );
}

function QuickCustomerDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState({ surname: "", name: "", phone: "", email: "", ragione_sociale: "", piva: "", codice_fiscale: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) setForm({ surname: "", name: "", phone: "", email: "", ragione_sociale: "", piva: "", codice_fiscale: "" });
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.surname.trim() || !form.phone.trim()) {
      return toast.error("Cognome, nome e telefono obbligatori");
    }
    setSaving(true);
    try {
      const { data } = await api.post("/customers", {
        name: form.name.trim(),
        surname: form.surname.trim(),
        phone: form.phone.trim(),
        email: form.email.trim() || null,
        ragione_sociale: form.ragione_sociale.trim() || null,
        piva: form.piva.trim() || null,
        codice_fiscale: form.codice_fiscale.trim() || null,
      });
      onCreated(data.id);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="quick-customer-dialog">
        <DialogHeader>
          <DialogTitle>Nuovo cliente rapido</DialogTitle>
          <DialogDescription>Dati minimi. Potrai completarli dopo dalla scheda cliente.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="font-mono text-[10px] uppercase tracking-widest">Cognome *</Label>
              <Input required value={form.surname} onChange={(e) => setForm({ ...form, surname: e.target.value })} className="mt-1.5" data-testid="qc-surname" />
            </div>
            <div>
              <Label className="font-mono text-[10px] uppercase tracking-widest">Nome *</Label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" data-testid="qc-name" />
            </div>
          </div>
          <div>
            <Label className="font-mono text-[10px] uppercase tracking-widest">Telefono *</Label>
            <Input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5 font-mono" data-testid="qc-phone" />
          </div>
          <div>
            <Label className="font-mono text-[10px] uppercase tracking-widest">Email</Label>
            <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" data-testid="qc-email" />
          </div>
          <div>
            <Label className="font-mono text-[10px] uppercase tracking-widest">Ragione sociale</Label>
            <Input value={form.ragione_sociale} onChange={(e) => setForm({ ...form, ragione_sociale: e.target.value })} className="mt-1.5" data-testid="qc-ragione" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="font-mono text-[10px] uppercase tracking-widest">P.IVA</Label>
              <Input value={form.piva} onChange={(e) => setForm({ ...form, piva: e.target.value })} className="mt-1.5 font-mono" data-testid="qc-piva" />
            </div>
            <div>
              <Label className="font-mono text-[10px] uppercase tracking-widest">Codice Fiscale</Label>
              <Input value={form.codice_fiscale} onChange={(e) => setForm({ ...form, codice_fiscale: e.target.value })} className="mt-1.5 font-mono" data-testid="qc-cf" />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Annulla</Button>
            <Button type="submit" disabled={saving} className="rounded-full gap-2" data-testid="qc-submit">
              <FloppyDisk size={16} /> {saving ? "Salvo…" : "Crea"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
